import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import { DataGrid } from '@mui/x-data-grid';
import useApiManager from '../../utils/Api/apiManager';
import { useNavigate } from "react-router-dom";
import { InvitationStatus, UserRoles } from '../../utils/enums';
import ConfirmationDialog from '../ConfirmationDialog/ConfirmationDialog';
import { toast } from 'sonner';

const InvitationsDataGrid = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [dialogAction, setDialogAction] = useState(''); // Tracks whether the dialog is for "reinvite" or "delete"
    const navigate = useNavigate();
    const { get, put, del } = useApiManager();

    const handleOpenDialog = (id, action) => {
        setSelectedId(id);
        setDialogAction(action);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedId(null);
        setDialogAction('');
    };

    const handleConfirmAction = async () => {
        if (selectedId) {
            try {
                if (dialogAction === 'reinvite') {
                    await put(`/invitations/${selectedId}`);
                    toast.success("Reinvitation sent successfully!");
                } else if (dialogAction === 'delete') {
                    await del(`/invitations/${selectedId}`);
                    setRows((prevRows) => prevRows.filter((row) => row.id !== selectedId)); // Remove deleted row
                }
            } catch (error) {
                console.error(`Failed to ${dialogAction} user:`, error);
            } finally {
                handleCloseDialog();
            }
        }
    };

    const columns = [
        { field: 'id', headerName: 'ID', flex: 0.5 },
        { field: 'firstName', headerName: 'First Name', flex: 1 },
        { field: 'lastName', headerName: 'Last Name', flex: 1 },
        { field: 'email', headerName: 'Email', flex: 1 },
        { field: 'role', headerName: 'Role', flex: 1 },
        { field: 'status', headerName: 'Status', flex: 1 },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 1,
            renderCell: (params) => (
                <div style={{ display: 'flex', gap: '10px' }}>
                    {params.row.rawStatus !== 1 && (
                        <div>
                            <button
                                onClick={() => handleOpenDialog(params.row.id, 'reinvite')}
                                style={{
                                    cursor: 'pointer',
                                    color: 'blue',
                                    background: 'none',
                                    border: 'none',
                                }}
                            >
                                Reinvite
                            </button>
                            <button
                                onClick={() => handleOpenDialog(params.row.id, 'delete')}
                                style={{
                                    cursor: 'pointer',
                                    color: 'red',
                                    background: 'none',
                                    border: 'none',
                                }}
                            >
                                Delete
                            </button>
                        </div>
                    )}

                </div>
            ),
        },
    ];

    const transformData = (data) => {
        return data.map((item) => ({
            id: item.id,
            firstName: item.firstName,
            lastName: item.lastName,
            email: item.email,
            role: UserRoles[item.role]?.label || 'Unknown',
            status: InvitationStatus[item.status]?.label || 'Unknown',
            rawStatus: item.status,
        }));
    };

    const fetchAndSetData = async () => {
        setLoading(true);
        try {
            const data = await get('/invitations/');
            setRows(transformData(data));
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {

        fetchAndSetData();
    }, []);

    // const handleRowClick = (params) => {
    //     navigate(`/admin/${params.id}`);
    // };

    return (
        <Box sx={{ height: '100%' }}>
            <DataGrid
                rows={rows}
                columns={columns}
                disableRowSelectionOnClick
                columnVisibilityModel={{
                    id: false,
                }}
                getRowId={(row) => row.id}
                initialState={{
                    pagination: {
                        paginationModel: {
                            pageSize: 50,
                        },
                    },
                }}
                pageSizeOptions={[50, 75, 100]}
                loading={loading}
                sx={{
                    height: '100%', // Fills the parent container
                    '& .MuiDataGrid-virtualScroller': {
                        overflow: 'auto', // Ensures scrollbars appear when necessary
                    },
                    '& .MuiDataGrid-row:hover': {
                        backgroundColor: 'transparent', // Disables hover background color
                    },
                }}
            />
            <ConfirmationDialog
                open={openDialog}
                title={dialogAction === 'delete' ? 'Delete Invitation' : 'Reinvite User'}
                content={
                    dialogAction === 'delete'
                        ? 'Are you sure you want to delete this invitation?'
                        : 'Are you sure you want to reinvite this user?'
                }
                onClose={handleCloseDialog}
                onConfirm={handleConfirmAction}
                confirmText={dialogAction === 'delete' ? 'Delete' : 'Confirm'}
                cancelText="Cancel"
            />
        </Box>
    );
};

export default InvitationsDataGrid;